// src/components/forms/LoginForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useModal } from '@/contexts/ModalContext';
import { useGuest } from '@/contexts/GuestContext';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { motion } from 'framer-motion';
import { useTenant } from '@/contexts/TenantContext';

export const LoginForm = () => {
  const { error, handleLogin } = useAuth();
  const { openModal } = useModal();
  const { enterGuestMode } = useGuest();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // If redirected here from a guest-blocked page, show a nudge
  const guestBlocked = location.state?.guestBlocked;


  const sessionExpired = new URLSearchParams(location.search).get('session_expired');

  useEffect(() => {
    localStorage.removeItem('accessToken');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await handleLogin(email, password);
      navigate('/competitions');
    } catch (error) {
      // Error handled by AuthContext
    }
  };

  const handleContinueAsGuest = () => {
    enterGuestMode();
    navigate('/competitions');
  };

const { tenant } = useTenant();
const tenantSlug = tenant?.slug || 'app';
const googleEnabled = tenant?.google_login_enabled !== false;

const handleGoogleLogin = () => {
  const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8000';
  window.location.href = `${apiBase}/api/auth/google/login/?tenant=${tenantSlug}`;
};

  return (
    <motion.div
      className="flex flex-col gap-5"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      {/* Nudge if redirected from a guest-blocked page */}
      {guestBlocked && (
        <div className="bg-mikado-yellow/10 border border-mikado-yellow/30 rounded-lg px-4 py-3 text-sm text-mikado-yellow text-center">
          Log in to access that feature.
        </div>
      )}

      {sessionExpired && (
        <div className="bg-imperial-red/10 border border-imperial-red/30 rounded-lg px-4 py-3 text-sm text-imperial-red text-center">
          Your session expired. Please log in again.
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {error && <p className="text-red-500 text-sm">{error}</p>}

        <Input
          type="text"
          placeholder="Username or Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <PasswordInput
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
        />

        <div className="flex items-center justify-between text-sm text-field-grey">
          <label className="flex items-center space-x-2">
            <input type="checkbox" className="form-checkbox" />
            <span>Remember me</span>
          </label>

          <button
            type="button"
            onClick={() => openModal('FORGOT_PASSWORD')}
            className="text-mikado-yellow hover:underline cursor-pointer transition-colors"
          >
            Forgot password?
          </button>
        </div>

        <Button
          type="submit"
          className="w-full bg-imperial-red text-white hover:bg-opacity-90 transition-colors"
        >
          Log In
        </Button>
      </form>

   {/* Divider */}
<div className="flex items-center gap-3 -mt-1">
  <div className="flex-1 h-px bg-pebble-brown/30" />
  <span className="text-xs text-swan/40">or</span>
  <div className="flex-1 h-px bg-pebble-brown/30" />
</div>

{/* Google Sign In */}
{googleEnabled && (
  <button
    type="button"
    onClick={handleGoogleLogin}
    className="w-full flex items-center justify-center gap-3 border border-pebble-brown/30 rounded-lg py-2.5 px-4 text-sm text-swan/80 hover:bg-pebble-brown/10 transition-colors -mt-1"
  >
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 6.293C4.672 4.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
    Continue with Google
  </button>
)}

{/* Continue as Guest — no divider needed, visually distinct enough */}
<button
  type="button"
  onClick={handleContinueAsGuest}
  className="w-full text-sm text-swan/50 hover:text-swan/80 transition-colors py-1 -mt-1"
>
  Continue as guest
</button>

<p className="text-xs text-swan/30 text-center -mt-3">
  Guests can browse photos but cannot vote or submit.
</p>
    </motion.div>
  );
};